import { useState, useCallback, useRef, useEffect } from "react"

const STEP_INTERVAL = 900 // ms per step during auto-play

export function usePlayback(totalSteps: number) {
  const [stepIndex, setStepIndex] = useState(-1) // -1 = initial state (before any step)
  const [isPlaying, setIsPlaying] = useState(false)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const stepForward = useCallback(() => {
    setStepIndex((prev) => {
      if (prev >= totalSteps - 1) {
        setIsPlaying(false)
        clearTimer()
        return prev
      }
      return prev + 1
    })
  }, [totalSteps, clearTimer])

  const stepBack = useCallback(() => {
    setIsPlaying(false)
    clearTimer()
    setStepIndex((prev) => Math.max(-1, prev - 1))
  }, [clearTimer])

  const play = useCallback(() => {
    setIsPlaying(true)
  }, [])

  const pause = useCallback(() => {
    setIsPlaying(false)
    clearTimer()
  }, [clearTimer])

  const reset = useCallback(() => {
    setIsPlaying(false)
    clearTimer()
    setStepIndex(-1)
  }, [clearTimer])

  // Auto-advance when playing
  useEffect(() => {
    if (isPlaying) {
      timerRef.current = setInterval(() => {
        setStepIndex((prev) => {
          if (prev >= totalSteps - 1) {
            setIsPlaying(false)
            return prev
          }
          return prev + 1
        })
      }, STEP_INTERVAL)
    } else {
      clearTimer()
    }
    return clearTimer
  }, [isPlaying, totalSteps, clearTimer])

  // Stop playing if we reach the end
  useEffect(() => {
    if (stepIndex >= totalSteps - 1) {
      setIsPlaying(false)
    }
  }, [stepIndex, totalSteps])

  return {
    stepIndex,
    isPlaying,
    atStart: stepIndex <= -1,
    atEnd: stepIndex >= totalSteps - 1,
    stepForward,
    stepBack,
    play,
    pause,
    reset,
  }
}
